import { createContext, useContext, useState, useEffect } from 'react'
import { 
  CognitoIdentityProviderClient, 
  InitiateAuthCommand, 
  SignUpCommand, 
  ConfirmSignUpCommand,
  GetUserCommand 
} from '@aws-sdk/client-cognito-identity-provider'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

const cognitoClient = new CognitoIdentityProviderClient({
  region: import.meta.env.VITE_AWS_REGION
})

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken'))
  const [isAdmin, setIsAdmin] = useState(false)

  // List of admin email addresses
  const ADMIN_EMAILS = [
    'info@kapcheboin.com', // Super admin
    'admin@kapcheboin.com',
    'bonakip@gmail.com', // Add your email here
    // Add more admin emails as needed
  ]

  useEffect(() => {
    checkAuthState()
  }, [])

  const checkAuthState = async () => {
    try {
      const token = localStorage.getItem('accessToken')
      if (token) {
        const command = new GetUserCommand({
          AccessToken: token
        })
        const response = await cognitoClient.send(command)
        
        const userAttributes = {}
        response.UserAttributes.forEach(attr => {
          userAttributes[attr.Name] = attr.Value
        })
        
        const userInfo = {
          username: response.Username,
          email: userAttributes.email,
          firstName: userAttributes.given_name,
          lastName: userAttributes.family_name,
          attributes: userAttributes
        }
        
        setUser(userInfo)
        setAccessToken(token)
        
        // Check admin status
        const adminStatus = ADMIN_EMAILS.includes(userAttributes.email?.toLowerCase())
        setIsAdmin(adminStatus)
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      setAccessToken(null)
    } finally {
      setLoading(false)
    }
  }

  const signUp = async (email, password, firstName, lastName) => {
    try {
      const command = new SignUpCommand({
        ClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
        Username: email,
        Password: password,
        UserAttributes: [
          { Name: 'email', Value: email },
          { Name: 'given_name', Value: firstName },
          { Name: 'family_name', Value: lastName }
        ]
      })
      
      const response = await cognitoClient.send(command)
      return { success: true, userSub: response.UserSub }
    } catch (error) {
      console.error('Sign up error:', error)
      throw new Error(error.message || 'Sign up failed')
    }
  }

  const confirmSignUp = async (email, confirmationCode) => {
    try {
      const command = new ConfirmSignUpCommand({
        ClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
        Username: email,
        ConfirmationCode: confirmationCode
      })
      
      await cognitoClient.send(command)
      return { success: true }
    } catch (error) {
      console.error('Confirmation error:', error)
      throw new Error(error.message || 'Confirmation failed')
    }
  }

  const signIn = async (email, password) => {
    try {
      const command = new InitiateAuthCommand({
        ClientId: import.meta.env.VITE_USER_POOL_CLIENT_ID,
        AuthFlow: 'USER_PASSWORD_AUTH',
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password
        }
      })
      
      const response = await cognitoClient.send(command)
      
      if (response.AuthenticationResult) {
        const { AccessToken, RefreshToken } = response.AuthenticationResult
        
        localStorage.setItem('accessToken', AccessToken)
        localStorage.setItem('refreshToken', RefreshToken)
        setAccessToken(AccessToken)
        
        await checkAuthState()
        return { success: true }
      }
    } catch (error) {
      console.error('Sign in error:', error)
      throw new Error(error.message || 'Sign in failed')
    }
  }

  const signOut = () => {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    setUser(null)
    setAccessToken(null)
    setIsAdmin(false)
  }

  const getAuthToken = async () => {
    return accessToken
  }

  const value = {
    user,
    loading,
    accessToken,
    isAdmin,
    signUp,
    confirmSignUp,
    signIn,
    signOut,
    getAuthToken,
    isAuthenticated: !!user
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}